import { useState, useEffect, useRef } from "react";
import { 
  subscribeToCustomers, 
  subscribeToCustomerCategories, 
  seedDefaultCustomersIfEmpty,
  addCustomer,
  updateCustomer,
  deleteCustomer,
  subscribeToPaymentMethods
} from "../services/customerService";

export function useCustomers() {
  const [customers, setCustomers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  // To avoid seeding multiple times if component re-renders quickly
  const hasSeededRef = useRef(false);

  useEffect(() => {
    let unsubscribeCustomers = () => {};
    let unsubscribeCategories = () => {};
    let unsubscribePaymentMethods = () => {};

    try {
      setLoading(true);

      // Subscribe to collections
      unsubscribeCustomers = subscribeToCustomers(async (data, err) => {
        if (err) {
          setError("Failed to load customers.");
          setLoading(false);
          return;
        }

        // If list is empty, seed defaults
        if (data.length === 0 && !hasSeededRef.current) {
          hasSeededRef.current = true;
          await seedDefaultCustomersIfEmpty(data);
          // the seeding will trigger another snapshot, which will update state
        } else {
          setCustomers(data);
          setLoading(false);
        }
      });

      unsubscribeCategories = subscribeToCustomerCategories((data, err) => {
        if (!err) setCategories(data);
      });

      unsubscribePaymentMethods = subscribeToPaymentMethods((data, err) => {
        if (!err) setPaymentMethods(data);
      });

    } catch (err) {
      console.error(err);
      setError("Failed to initialize customer subscriptions.");
      setLoading(false);
    }

    return () => {
      unsubscribeCustomers();
      unsubscribeCategories();
      unsubscribePaymentMethods();
    };
  }, []);

  const handleAddCustomer = async (customerData) => {
    try {
      setError("");
      await addCustomer(customerData);
    } catch (err) {
      console.error("Error adding customer:", err);
      setError(err.message || "Failed to add customer.");
      throw err;
    }
  };

  const handleUpdateCustomer = async (id, customerData) => {
    try {
      setError("");
      await updateCustomer(id, customerData);
    } catch (err) {
      console.error("Error updating customer:", err);
      setError(err.message || "Failed to update customer.");
      throw err;
    }
  };

  const handleDeleteCustomer = async (id) => {
    try {
      setError("");
      await deleteCustomer(id);
    } catch (err) {
      console.error("Error deleting customer:", err);
      setError(err.message || "Failed to delete customer.");
      throw err;
    }
  };

  return {
    customers,
    categories,
    paymentMethods,
    loading,
    error,
    setError,
    addCustomer: handleAddCustomer,
    updateCustomer: handleUpdateCustomer,
    deleteCustomer: handleDeleteCustomer
  };
}
